import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import { getBatchShipmentsApi } from 'api/order';
import { groupBy } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

const BatchRollbackDialog = ({ open, onClose, onConfirm, batchShipmentId, currentOrderId }) => {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    if (!open || !batchShipmentId) return;
    setLoading(true);
    getBatchShipmentsApi({ batchShipmentId })
      .then(({ response }) => {
        const grouped = groupBy(response, 'orderId');
        const aggregatedOrders = Object.entries(grouped).map(([orderId, items]) => ({
          orderId,
          patientFirstName: items[0].patientFirstName,
          patientLastName: items[0].patientLastName,
          drugNames: items.map((item) => item.drugName).filter(Boolean),
        }));
        setOrders(aggregatedOrders);
        setSelected([currentOrderId]);
      })
      .catch(() => {
        setOrders([]);
        setSelected([currentOrderId]);
      })
      .finally(() => setLoading(false));
  }, [open, batchShipmentId, currentOrderId]);

  const handleToggle = (orderId) => {
    setSelected((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
  };

  const renderOrderList = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }
    if (orders.length === 0) {
      return (
        <Typography variant='body2' color='text.secondary'>
          No orders found in this batch.
        </Typography>
      );
    }
    return (
      <FormGroup>
        {orders.map(({ orderId, patientFirstName, patientLastName, drugNames }) => (
          <FormControlLabel
            key={orderId}
            control={
              <Checkbox
                checked={selected.includes(orderId)}
                onChange={() => handleToggle(orderId)}
              />
            }
            label={
              <Box>
                <Typography variant='body2' component='span' fontWeight='medium'>
                  {orderId}
                </Typography>
                <Typography variant='body2' component='span' color='text.secondary'>
                  {' '}
                  — {patientFirstName} {patientLastName}
                  {drugNames.length > 0 ? ` · ${drugNames.join(', ')}` : ''}
                </Typography>
              </Box>
            }
          />
        ))}
      </FormGroup>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Send Order(s) Back To Queue</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Select the orders from this batch you want to send back to the queue. Only the selected
          orders will be rolled back.
        </DialogContentText>
        {renderOrderList()}
      </DialogContent>
      <DialogActions>
        <LoadingButton
          variant='contained'
          color='error'
          loading={isConfirming}
          disabled={selected.length === 0 || loading}
          onClick={() => {
            setIsConfirming(true);
            onConfirm(selected).catch(() => setIsConfirming(false));
          }}
        >
          Send Back to Queue
        </LoadingButton>
        <Button onClick={onClose} variant='outlined' disabled={isConfirming}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

BatchRollbackDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  batchShipmentId: PropTypes.string,
  currentOrderId: PropTypes.string.isRequired,
};

BatchRollbackDialog.defaultProps = {
  batchShipmentId: undefined,
};

export default BatchRollbackDialog;
